import { reportRefreshedTokenRejected } from '@/model/client';
import { logger } from '@/shared/helpers/logger';
import { delayUntilAborted, nextBackoffMs } from '@/viewmodel/features/etlSource/backoff';
import {
  markStreamAvailable,
  markStreamRefused,
} from '@/viewmodel/features/etlSource/state/streamAvailability';

import {
  FatalStreamError,
  type SseCallbacks,
  StreamRefusedError,
  TokenRefreshedError,
  openStreamSession,
} from '@/model/etlSource/api';

const STOPPED_REPROBE_MS = 5 * 60_000;

function stopLiveUpdates(error: unknown): void {
  logger.warn('live updates stopped', { error, log_source: 'liveUpdates' });
  markStreamRefused(STOPPED_REPROBE_MS);
}

async function reconnectLoop(
  projectId: string,
  getToken: () => string | null,
  callbacks: SseCallbacks,
  signal: AbortSignal,
): Promise<void> {
  let attempt = 0;
  let onRefreshedToken = false;

  while (!signal.aborted) {
    try {
      await openStreamSession(projectId, getToken(), callbacks, signal, () => {
        attempt = 0;
        onRefreshedToken = false;
        markStreamAvailable();
      });

      return;
    } catch (error) {
      if (signal.aborted) return;

      callbacks.onStateChange?.('closed');

      if (error instanceof StreamRefusedError) {
        logger.warn('live updates refused; falling back to polling', {
          error,
          log_source: 'liveUpdates',
          retry_after_ms: error.retryAfterMs,
        });
        markStreamRefused(error.retryAfterMs);

        return;
      }

      if (error instanceof FatalStreamError) {
        stopLiveUpdates(error);

        return;
      }

      if (error instanceof TokenRefreshedError) {
        if (onRefreshedToken) {
          reportRefreshedTokenRejected();
          stopLiveUpdates(error);

          return;
        }

        onRefreshedToken = true;
      }

      await delayUntilAborted(nextBackoffMs(attempt), signal);
      attempt += 1;
    }
  }
}

export function subscribeEtlEvents(
  projectId: string,
  getToken: () => string | null,
  callbacks: SseCallbacks,
): () => void {
  const controller = new AbortController();

  void reconnectLoop(projectId, getToken, callbacks, controller.signal);

  return (): void => controller.abort();
}
